import { useMemo, useState, type ReactNode } from 'react';
import type { ComponentManifest, ViewResolution } from '@ayumia/contracts';
import { collectionInputs, entryRepresentation, isCollectionRepresentation, isCoreRepresentation } from '@ayumia/component-runtime';
import { text } from '../app/i18n';
import { Dialog, Drawer } from '../design-system/Overlay';
import { Empty, errorView, Loading } from '../states/States';
import { ActionBar } from './ActionBar';
import { formOf } from './assembly';
import { DashboardRepresentation } from './DashboardRepresentation';
import { Btn, PageHeader, Pill } from '../design-system/primitives';
import { CommandDialog } from './CommandDialog';
import { DetailRepresentation } from './DetailRepresentation';
import { FormRepresentation } from './FormRepresentation';
import { ListRepresentation } from './ListRepresentation';
import { useLoad, type Load } from './useLoad';
import { AppServicesContext, useAppServices } from './ViewContext';

/**
 * Une page = une `View` résolue par `GetView` en **une** réponse (ADR-050) : la vue, l'objet et
 * ses composants. La vue est un assemblage de composants (ADR-099) : la page rend l'instance
 * d'entrée — le conteneur `Collection` pour une représentation de collection, sinon `Detail`,
 * `Form` ou `Dashboard` — et toutes partagent cet objet et cette source ; aucune représentation
 * ne résout sa source de son côté (59 §20.1). Le détail s'ouvre dans un tiroir
 * droit qui garde la liste intacte (59 §6.3) ; la modification passe par un Dialog et la commande
 * de la vue.
 */

type Row = Record<string, unknown> & { id?: string; version?: number };

/**
 * Services de l'application portés par une vue résolue : ses composants, leur exécution `run` et la charge
 * utile déclarée de ses commandes (ADR-064 §3) — l'ActionBar n'en devine aucune. Partagé par la page et par
 * le tiroir de fiche ouvert hors d'une liste (explorateur du graphe, ADR-129).
 */
export function useViewServices(resolution: Load<ViewResolution>): { scoped: ReturnType<typeof useAppServices>; commands: ReadonlyMap<string, NonNullable<ViewResolution['commands']>[number]> } {
  const services = useAppServices();
  const components = useMemo(() => {
    const map = new Map<string, ComponentManifest>(services.components);
    if (resolution.status === 'ready') for (const component of resolution.value.components) map.set(component.artifact_id, component.body);
    return map;
  }, [resolution, services.components]);
  const componentRun = useMemo(() => {
    const map = new Map<string, boolean>(services.componentRun);
    if (resolution.status === 'ready') for (const component of resolution.value.components) if (component.run_enabled !== undefined) map.set(component.artifact_id, component.run_enabled);
    return map;
  }, [resolution, services.componentRun]);
  const commands = useMemo(() => new Map(resolution.status === 'ready' ? (resolution.value.commands ?? []).map((entry) => [entry.command_name, entry] as const) : []), [resolution]);
  const scoped = useMemo(() => ({ ...services, components, commands, componentRun }), [services, components, commands, componentRun]);
  return { scoped, commands };
}

export type ViewSource = { view_ref: string } | { object_type: string };

export function ViewPage({
  source,
  recordId,
  onRecordChange,
  fallback,
  listState,
  onListStateChange,
}: {
  source: ViewSource;
  recordId?: string;
  onRecordChange: (recordId: string | undefined) => void;
  /** Rendu de repli quand la vue ne se résout pas (accueil sans tableau de bord, ADR-092 §5). */
  fallback?: (error: { category: string; code: string; message: string }) => ReactNode;
  /** Surtitre de l'en-tête : le module de navigation qui mène à la vue. */
  /** État de la liste sérialisé dans l'URL (ADR-094) et son écriture. */
  listState?: string;
  onListStateChange?: (next: string, options: { replace: boolean }) => void;
}) {
  const services = useAppServices();
  const key = 'view_ref' in source ? `view:${source.view_ref}` : `object:${source.object_type}`;
  const [resolution, reload] = useLoad<ViewResolution>(key, async () => (await services.client.query<ViewResolution>('GetView', source as Record<string, string>)).data);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState<{ record: Row | null } | null>(null);
  const [creating, setCreating] = useState(false);

  const { scoped, commands } = useViewServices(resolution);

  if (resolution.status === 'loading') return <Loading />;
  if (resolution.status === 'error') return <>{fallback === undefined ? errorView(resolution.error, reload) : fallback(resolution.error)}</>;
  const { view, object } = resolution.value;
  const body = view.body;
  const title = text(body.title, 'fr', object === undefined ? '' : text(object.plural_label ?? object.label));
  const changed = (): void => setRefreshKey((value) => value + 1);

  // Actions de collection de la vue dans l'en-tête ; les actions d'enregistrement vivent dans le détail.
  // Création (ADR-094) : la commande déclarée par le conteneur, saisie par sa charge utile ; sinon le formulaire de la vue.
  const entry = entryRepresentation(body);
  const form = formOf(body);
  const createAction = collectionInputs(body).create_action;
  const createCommand = createAction?.command_name === undefined ? undefined : commands.get(createAction.command_name);
  const createLabel = createAction === undefined ? 'Nouveau' : text(createAction.label);
  let headerActions: ReactNode;
  let content;
  // Table, cartes, Kanban et Agenda : un seul conteneur Collection, qui offre aussi les autres représentations de la vue (ADR-096, ADR-099).
  if (entry !== undefined && isCollectionRepresentation(entry.component)) {
    headerActions = (
      <>
        <ActionBar actions={body.actions ?? []} record={null} target={null} onDone={changed} />
        {createCommand !== undefined ? (
          <Btn variant="dark" icon="plus" onClick={() => setCreating(true)}>
            {createLabel}
          </Btn>
        ) : (
          form !== undefined &&
          form.submit_command !== undefined && (
            <Btn variant="dark" icon="plus" onClick={() => setEditing({ record: null })}>
              {createLabel}
            </Btn>
          )
        )}
      </>
    );
    content = (
      <>
        <ListRepresentation
          view={body}
          object={object}
          refreshKey={refreshKey}
          selectedId={recordId ?? null}
          onOpen={(row) => typeof row.id === 'string' && onRecordChange(row.id)}
          {...(resolution.value.list_export === undefined ? {} : { listExport: resolution.value.list_export })}
          {...(onListStateChange === undefined ? {} : { state: listState ?? '', onStateChange: onListStateChange })}
        />
        <Drawer title={object === undefined ? 'Détail' : text(object.label)} open={recordId !== undefined} onClose={() => onRecordChange(undefined)}>
          {recordId !== undefined && <DetailRepresentation view={body} object={object} recordId={recordId} onChanged={changed} onEdit={(record) => setEditing({ record })} />}
        </Drawer>
      </>
    );
  } else if (entry !== undefined && isCoreRepresentation(entry, 'Detail')) {
    content = recordId === undefined ? <Empty label="Aucun enregistrement sélectionné." /> : <DetailRepresentation view={body} object={object} recordId={recordId} onChanged={changed} onEdit={(record) => setEditing({ record })} />;
  } else if (entry !== undefined && isCoreRepresentation(entry, 'Form')) {
    content = <FormRepresentation view={body} form={entry.inputs} object={object} record={null} onSaved={changed} onCancel={() => onRecordChange(undefined)} />;
  } else if (entry !== undefined && isCoreRepresentation(entry, 'Dashboard')) {
    content = <DashboardRepresentation dashboard={entry.inputs} />;
  } else {
    content = <Empty label={`Le composant « ${entry?.component ?? body.entry} » n'a pas encore de rendu de page.`} />;
  }

  return (
    <AppServicesContext.Provider value={scoped}>
      <section className="ay-view" aria-labelledby="ay-view-title">
        <PageHeader
          titleId="ay-view-title"
          title={title}
          {...(body.description === undefined ? {} : { subtitle: text(body.description) })}
          {...(view.derived ? { badge: <Pill tone="neutral">Vue par défaut</Pill> } : {})}
          {...(headerActions === undefined ? {} : { actions: headerActions })}
        />
        {content}
        {creating && createCommand !== undefined && (
          <CommandDialog
            command={createCommand}
            prefilled={{}}
            target={null}
            onDone={() => {
              setCreating(false);
              changed();
            }}
            onClose={() => setCreating(false)}
          />
        )}
        {editing !== null && form !== undefined && (
          <Dialog title={editing.record === null ? 'Nouveau' : 'Modifier'} open onClose={() => setEditing(null)} size="lg">
            <FormRepresentation
              view={body}
              form={form}
              object={object}
              record={editing.record}
              onSaved={() => {
                setEditing(null);
                changed();
              }}
              onCancel={() => setEditing(null)}
            />
          </Dialog>
        )}
      </section>
    </AppServicesContext.Provider>
  );
}
